import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import dayjs, {
  Dayjs,
  getOrgNow,
} from 'utils/dayjs';
import {
  isEmptyString,
  pluralize,
} from '@populateui/utils';
import {
  DateRange,
} from 'types/dateRange';

type DateParam = number | string | Date | Dayjs;

const allowedFormats = [
  'MM/DD/YYYY',
  'MM/DD/YY',
  'M/D/YYYY',
  'M/D/YY',
  'YYYY-MM-DD',
  'MM-DD-YYYY',
  'M-D-YYYY',
];

function _formatDate(
  format: string,
  date: DateParam,
  keepLocal: boolean = true,
) {
  const dateObject = dayjs(date);

  const formattedDate = dateObject.year() <= 1941
    ? dateObject.format(format)
    : dateObject.tz(undefined, keepLocal).format(format);

  return dateObject.isValid() ? formattedDate : '';
}

export const formatDate = _formatDate.bind(null, 'M/D/YYYY');
export const formatDateTime = _formatDate.bind(null, 'M/D/YYYY h:mma');
export const formatTime = _formatDate.bind(null, 'h:mma');
export const formatDateOnly = _formatDate.bind(null, 'YYYY-MM-DD');
export const formatDateTimeOffset = _formatDate.bind(null, '');
export const formatDay = _formatDate.bind(null, 'ddd D');
export const formatMonth = _formatDate.bind(null, 'MMMM YYYY');
export const formatYear = _formatDate.bind(null, 'YYYY');
export const formatISODateTime = _formatDate.bind(null, 'YYYY-MM-DDTHH:mm:ss');
export const formatTwoDigitDate = _formatDate.bind(null, 'MM/DD/YYYY');
export const formatToReadableDate = _formatDate.bind(null, 'DD MMM, YYYY');

export function formatSignatureTimestamp(date: Dayjs = getOrgNow()) {
  if (!date.isValid()) {
    return '';
  }

  const timezoneAbbreviation = date.offsetName();

  return timezoneAbbreviation
    ? `${date.format('M/D/YYYY h:mm A')} ${timezoneAbbreviation}`
    : date.format('M/D/YYYY h:mm A');
}

export const getDateRangeFromStringParam = (param: string): DateRange | undefined => {
  const parts = param.split('--');
  if (parts.length !== 2 || parts.some(isEmptyString)) {
    return undefined;
  }

  const [min, max] = parts;
  const earliest = dayjs(min, 'YYYY-MM-DD', true).startOf('day');
  const latest = dayjs(max, 'YYYY-MM-DD', true).endOf('day');

  if (!earliest.isValid() || !latest.isValid() || latest.isBefore(earliest)) {
    return undefined;
  }

  return {
    min: earliest.toDate(),
    max: latest.toDate(),
  };
};

const timeUnits: string[] = ['hour', 'minute'];
export function getTimeDifferenceString(from: Dayjs, to: Dayjs): string {
  const timeDiff = [
    to.diff(from, 'hours'),
    to.diff(from, 'minutes') % 60,
  ];

  const timeValues = timeDiff.map((diffValue, index) => (
    diffValue > 0 ? `${pluralize(timeUnits[index], diffValue)}` : null
  ));

  return `(${compact(timeValues).join(' ')})`;
}

export function getTimeRangeString(from: Dayjs, to: Dayjs): string {
  return `${formatTime(from)} - ${formatTime(to)}`;
}

export function getDateRangeString(from: Dayjs, to: Dayjs): string {
  return `${formatDate(from)}${from.isSame(to, 'day') ? '' : ` - ${formatDate(to)}`}`;
}

export function isValidDateRange(
  range: Partial<DateRange> | null | undefined,
): range is DateRange {
  return !isNil(range?.min)
    && !isNil(range?.max)
    && dayjs(range.min).isValid()
    && dayjs(range.max).isValid();
}

export function validateDateTimeRange([start, end]: Dayjs[]): boolean {
  const normalizedStart = !isNil(start) ? dayjs(start.valueOf()).tz() : dayjs(null);
  const normalizedEnd = !isNil(end) ? dayjs(end.valueOf()).tz() : dayjs(null);

  return normalizedStart.isValid()
    && normalizedEnd.isValid()
    && normalizedStart.format('YYYY-MM-DD') === normalizedEnd.format('YYYY-MM-DD')
    // Compare instants: isAfter re-parses wall-clock time via the tz plugin's startOf,
    // which resolves the repeated hour on the DST fall-back night to the wrong offset.
    && normalizedEnd.valueOf() > normalizedStart.valueOf();
}

export function parseFlexibleDate(input: string): Dayjs | null {
  let parsedDate = null;

  if (!isEmptyString(input) && typeof input === 'string') {
    const cleanInput = input.trim();

    // Check if input matches MM/DD/YY format specifically
    const twoDigitYearMatch = cleanInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/) ?? [];

    const month = parseInt(twoDigitYearMatch[1], 10);
    const day = parseInt(twoDigitYearMatch[2], 10);
    const twoDigitYear = parseInt(twoDigitYearMatch[3], 10);
    const isValid = month > 0 && month <= 12 && day > 0 && day <= 31;

    if (twoDigitYearMatch && isValid) {
      const currentYear = dayjs().year();
      const currentTwoDigit = currentYear % 100;

      const decade = twoDigitYear <= currentTwoDigit ? 2000 : 1900;
      const fullYear = decade + twoDigitYear;

      const tentativeDate = dayjs(`${month}/${day}/${fullYear}`, 'M/D/YYYY', true);
      const isValidDate = tentativeDate.isValid()
                            && tentativeDate.month() === month - 1 // dayjs months are 0-indexed
                            && tentativeDate.date() === day
                            && tentativeDate.year() === fullYear;

      parsedDate = isValidDate ? tentativeDate : null;
    } else {
      // Only allow complete date formats, reject incomplete dates
      const validFormat = allowedFormats.find((format) => {
        const parsed = dayjs(cleanInput, format, true);
        return parsed.isValid();
      });

      // Only parse if it matches one of our allowed complete formats
      if (!isNil(validFormat)) {
        // Additional validation: ensure input has the right number of parts
        const separator = validFormat.includes('/') ? '/' : '-';
        const expectedParts = validFormat.split(separator).length;
        const actualParts = cleanInput.split(separator).length;

        // Also check that the input uses the same separator as the format
        let inputSeparator = '';
        if (cleanInput.includes('/')) {
          inputSeparator = '/';
        } else if (cleanInput.includes('-')) {
          inputSeparator = '-';
        }

        if (expectedParts === actualParts && separator === inputSeparator) {
          const parsed = dayjs(cleanInput, validFormat, true);
          parsedDate = parsed.isValid() ? parsed : null;
        }
      }
    }
  }

  return parsedDate;
}
